import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch } from '../api.js';
import { useAuth } from './AuthContext.jsx';
import { useFamily } from './FamilyContext.jsx';

const ProgressContext = createContext(null);

function getCurrentMember(members, userUuid) {
  return (members || []).find((member) => member.uuid === userUuid) || null;
}

export function ProgressProvider({ children }) {
  const { token, user } = useAuth();
  const { family, members } = useFamily();

  const currentMember = useMemo(
    () => getCurrentMember(members, user?.uuid),
    [members, user?.uuid]
  );

  // Signature of the profile fields that gate visibility; progression totals depend on it.
  const memberGateSignature = useMemo(() => {
    if (!currentMember) return '';
    return [
      currentMember.housing_type,
      currentMember.has_pets,
      currentMember.has_livestock,
      currentMember.has_vehicle,
      currentMember.cares_for_dependent,
      currentMember.children_full_time_count,
      currentMember.children_shared_custody_count
    ].join('|');
  }, [currentMember]);

  const [progress, setProgress] = useState(null);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const requestIdRef = useRef(0);
  const progressRef = useRef(null);

  useEffect(() => {
    progressRef.current = progress;
  }, [progress]);

  const canLoadProgress = Boolean(token && family && currentMember);

  const refreshProgress = useCallback(async ({ keepExisting = true } = {}) => {
    if (!canLoadProgress) {
      requestIdRef.current += 1;
      setProgress(null);
      setStatus('idle');
      setError('');
      return null;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    const currentProgress = progressRef.current;
    setStatus(currentProgress && keepExisting ? 'refreshing' : 'loading');
    setError('');

    try {
      const payload = await apiFetch('/api/progress', { token });

      if (requestIdRef.current !== requestId) {
        return payload;
      }

      setProgress(payload);
      setStatus('ready');
      return payload;
    } catch (err) {
      if (requestIdRef.current !== requestId) {
        throw err;
      }

      const message = err?.message || 'Impossible de charger la progression.';
      setError(message);

      if (currentProgress && keepExisting) {
        setStatus('ready');
      } else {
        setProgress(null);
        setStatus('error');
      }

      throw err;
    }
  }, [canLoadProgress, token]);

  useEffect(() => {
    if (!canLoadProgress) {
      requestIdRef.current += 1;
      setProgress(null);
      setStatus('idle');
      setError('');
      return;
    }

    setProgress(null);
    setStatus('loading');
    setError('');

    refreshProgress({ keepExisting: false }).catch(() => undefined);
  }, [canLoadProgress, refreshProgress, family?.uuid, currentMember?.uuid, memberGateSignature]);

  const value = useMemo(() => ({
    progress,
    status,
    error,
    refreshProgress,
    hasResolvedProgress: Boolean(progress),
    isLoading: status === 'loading' || status === 'refreshing'
  }), [error, progress, refreshProgress, status]);

  return <ProgressContext.Provider value={value}>{children}</ProgressContext.Provider>;
}

export function useProgress() {
  const context = useContext(ProgressContext);

  if (!context) {
    throw new Error('useProgress must be used within ProgressProvider');
  }

  return context;
}
